import { useEffect, useMemo, useState } from 'react'
import { useContentCpStore } from '@/stores/useContentCpStore'
import {
  fetchSessionCard,
  fetchSource,
  EDGE_TYPES,
  EDGE_TYPE_LABELS,
} from '@/lib/api'
import type { ContentCardDetail as ContentCardDetailType, SourceDetail } from '@/lib/api'
import { TypeBadge, DomainBadge } from '@/components/ui/Badge'
import {
  ArrowLeft,
  Loader2,
  FileText,
  Link as LinkIcon,
  ExternalLink,
} from 'lucide-react'

// Define edge type for session-scoped cards
interface SessionEdge {
  edge_id: string
  to_card_id?: string
  from_card_id?: string
  type: string
  weight: number
  to_title?: string | null
  to_summary?: string
  from_title?: string | null
  from_summary?: string
  linked_card_id: string
  linked_card_title: string | null
  linked_card_summary: string
}

interface ContentCpCardDetailProps {
  cardId: string | null
  sourceId: string | null
  onBack: () => void
}

export function ContentCpCardDetail({ cardId, sourceId, onBack }: ContentCpCardDetailProps) {
  const { sessionId, navigateToCard, navigateToSource } = useContentCpStore()
  const [card, setCard] = useState<ContentCardDetailType | null>(null)
  const [source, setSource] = useState<SourceDetail | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Fetch card or source based on what's selected
  useEffect(() => {
    setIsLoading(true)
    setError(null)

    if (sourceId) {
      // TODO: Create session-scoped source fetch endpoint
      fetchSource(sourceId)
        .then(setSource)
        .catch((e) => {
          setError(e instanceof Error ? e.message : 'Failed to load source')
        })
        .finally(() => setIsLoading(false))
    } else if (cardId && sessionId) {
      fetchSessionCard(sessionId, cardId)
        .then(setCard)
        .catch((e) => {
          setError(e instanceof Error ? e.message : 'Failed to load card')
        })
        .finally(() => setIsLoading(false))
    } else if (cardId && !sessionId) {
      setError('No session ID available')
      setIsLoading(false)
    }
  }, [cardId, sourceId, sessionId])

  // Transform edges to common format for display
  // Must be called before any conditional returns (Rules of Hooks)
  const transformedEdges = useMemo(() => {
    if (!card) return { incoming: [], outgoing: [] }
    const outgoing: SessionEdge[] = (card.edges_from || []).map((e) => ({
      ...e,
      linked_card_id: e.to_card_id || '',
      linked_card_title: e.to_title || null,
      linked_card_summary: e.to_summary || '',
    }))
    const incoming: SessionEdge[] = (card.edges_to || []).map((e) => ({
      ...e,
      linked_card_id: e.from_card_id || '',
      linked_card_title: e.from_title || null,
      linked_card_summary: e.from_summary || '',
    }))
    return { incoming, outgoing }
  }, [card])

  // Show source detail if sourceId is set
  if (sourceId) {
    return (
      <ContentCpSourceDetailView
        source={source}
        isLoading={isLoading}
        error={error}
        onBack={onBack}
        onCardClick={navigateToCard}
      />
    )
  }

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (error || !card) {
    return (
      <div className="h-full flex flex-col items-center justify-center p-4">
        <p className="text-sm text-muted-foreground mb-4">
          {error || 'Card not found'}
        </p>
        <button
          onClick={onBack}
          className="text-sm text-primary hover:underline flex items-center gap-1"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col">
      <CardInfoSection
        card={card}
        onBack={onBack}
        onSourceClick={navigateToSource}
      />
      <LinkTablesSection
        incomingEdges={transformedEdges.incoming}
        outgoingEdges={transformedEdges.outgoing}
        onCardClick={navigateToCard}
      />
    </div>
  )
}

function CardInfoSection({
  card,
  onBack,
  onSourceClick,
}: {
  card: ContentCardDetailType
  onBack: () => void
  onSourceClick: (sourceId: string) => void
}) {
  const linkCounts = useMemo(() => {
    const counts: Record<string, { incoming: number; outgoing: number }> = {}

    ;(card.edges_from || []).forEach((edge) => {
      if (!counts[edge.type]) counts[edge.type] = { incoming: 0, outgoing: 0 }
      counts[edge.type].outgoing++
    })

    ;(card.edges_to || []).forEach((edge) => {
      if (!counts[edge.type]) counts[edge.type] = { incoming: 0, outgoing: 0 }
      counts[edge.type].incoming++
    })

    return counts
  }, [card.edges_from, card.edges_to])

  type EdgeType = (typeof EDGE_TYPES)[number]

  const linkCountDisplay = useMemo(() => {
    const parts = Object.entries(linkCounts).map(([type, counts]) => {
      const total = counts.incoming + counts.outgoing
      const label = EDGE_TYPE_LABELS[type as EdgeType]
      return `${total} ${label?.toLowerCase() || type.replace('_', ' ')}`
    })
    return parts.join(', ')
  }, [linkCounts])

  const totalEdges = (card.edges_from || []).length + (card.edges_to || []).length

  const allTags = useMemo(() => {
    const tagSet = new Set<string>()
    card.tags?.forEach((tag) => tagSet.add(tag))
    return Array.from(tagSet)
  }, [card.tags])

  return (
    <header className="flex-shrink-0 border-b border-border px-4 py-4 bg-card">
      {/* Back button */}
      <button
        onClick={onBack}
        className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-3"
      >
        <ArrowLeft className="h-4 w-4" />
        Back
      </button>

      {/* Card Title */}
      {card.title && (
        <h2 className="text-lg font-medium mb-2">{card.title}</h2>
      )}

      {/* Badges */}
      <div className="flex items-center gap-1.5 mb-3">
        <TypeBadge type={card.type} />
        <DomainBadge domain={card.domain} />
      </div>

      {/* Summary */}
      <p className="text-sm leading-relaxed mb-3">{card.summary}</p>

      {/* Tags */}
      {allTags.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-3">
          {allTags.map((tag) => (
            <span key={tag} className="text-xs text-muted-foreground">
              #{tag}
            </span>
          ))}
        </div>
      )}

      {/* Link counts */}
      {totalEdges > 0 && (
        <div className="flex items-center gap-2 mb-2 text-xs text-muted-foreground">
          <LinkIcon className="h-3.5 w-3.5" />
          <span>{linkCountDisplay}</span>
        </div>
      )}

      {/* Source and date */}
      <div className="flex items-center gap-4 text-xs text-muted-foreground">
        {card.sources.length > 0 && (
          <button
            onClick={() => onSourceClick(card.sources[0].source_id)}
            className="flex items-center gap-1 hover:text-primary transition-colors"
          >
            <FileText className="h-3.5 w-3.5" />
            <span className="hover:underline">
              Source: {card.sources[0].title || card.sources[0].filename || 'Untitled'}
            </span>
            <ExternalLink className="h-3 w-3" />
          </button>
        )}
        <span>Created: {new Date(card.created_at).toLocaleDateString()}</span>
      </div>
    </header>
  )
}

function LinkTablesSection({
  incomingEdges,
  outgoingEdges,
  onCardClick,
}: {
  incomingEdges: SessionEdge[]
  outgoingEdges: SessionEdge[]
  onCardClick: (cardId: string) => void
}) {
  const edgesByType = useMemo(() => {
    const grouped: Record<string, SessionEdge[]> = {}

    EDGE_TYPES.forEach((type) => {
      grouped[type] = []
    })

    ;[...incomingEdges, ...outgoingEdges].forEach((edge) => {
      if (grouped[edge.type]) {
        grouped[edge.type].push(edge)
      }
    })

    return grouped
  }, [incomingEdges, outgoingEdges])

  const sortByWeight = (edges: SessionEdge[]) =>
    [...edges].sort((a, b) => b.weight - a.weight)

  const hasAnyEdges = incomingEdges.length > 0 || outgoingEdges.length > 0

  return (
    <div className="flex-1 overflow-auto px-4 py-4 pb-8 space-y-4">
      {!hasAnyEdges && (
        <div className="text-center text-sm text-muted-foreground py-6">
          No connections found
        </div>
      )}

      {EDGE_TYPES.map((type) => {
        const edges = edgesByType[type]
        if (edges.length === 0) return null

        const label = EDGE_TYPE_LABELS[type]

        return (
          <EdgeTable
            key={type}
            title={label}
            edges={sortByWeight(edges)}
            onCardClick={onCardClick}
          />
        )
      })}
    </div>
  )
}

function EdgeTable({
  title,
  edges,
  onCardClick,
}: {
  title: string
  edges: SessionEdge[]
  onCardClick: (cardId: string) => void
}) {
  return (
    <div className="space-y-2">
      <h3 className="text-sm font-medium flex items-center gap-2 border-b border-border pb-2">
        <LinkIcon className="h-3.5 w-3.5 text-muted-foreground" />
        {title}
        <span className="text-muted-foreground font-normal">({edges.length})</span>
      </h3>

      <div className="border border-border rounded overflow-hidden">
        <table className="w-full text-xs table-fixed">
          <thead className="bg-muted/50">
            <tr>
              <th className="px-2 py-1.5 text-left font-medium w-12">Wt</th>
              <th className="px-2 py-1.5 text-left font-medium w-32">Title</th>
              <th className="px-2 py-1.5 text-left font-medium">Summary</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {edges.map((edge) => (
              <tr
                key={edge.edge_id}
                className="hover:bg-muted/30 transition-colors align-top"
              >
                <td className="px-2 py-2 text-muted-foreground">
                  {Math.round(edge.weight * 100)}%
                </td>
                <td className="px-2 py-2">
                  <button
                    onClick={() => onCardClick(edge.linked_card_id)}
                    className="text-primary hover:underline font-medium text-left"
                  >
                    {edge.linked_card_title || 'Untitled'}
                  </button>
                </td>
                <td className="px-2 py-2 text-muted-foreground">
                  {edge.linked_card_summary}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// =============================================================================
// Source Detail View Component
// =============================================================================

interface ContentCpSourceDetailViewProps {
  source: SourceDetail | null
  isLoading: boolean
  error: string | null
  onBack: () => void
  onCardClick: (cardId: string) => void
}

function ContentCpSourceDetailView({ source, isLoading, error, onBack, onCardClick }: ContentCpSourceDetailViewProps) {
  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (error || !source) {
    return (
      <div className="h-full flex flex-col items-center justify-center p-4">
        <p className="text-sm text-muted-foreground mb-4">
          {error || 'Source not found'}
        </p>
        <button
          onClick={onBack}
          className="text-sm text-primary hover:underline flex items-center gap-1"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <header className="flex-shrink-0 border-b border-border px-4 py-4 bg-card">
        <button
          onClick={onBack}
          className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-3"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>

        <h2 className="text-lg font-medium mb-2 flex items-center gap-2">
          <FileText className="h-5 w-5 text-muted-foreground" />
          {source.title || source.filename || 'Untitled Source'}
        </h2>

        {/* Metadata */}
        <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
          {source.filename && (
            <span>File: {source.filename}</span>
          )}
          {source.ingested_at && (
            <span>Ingested: {new Date(source.ingested_at).toLocaleDateString()}</span>
          )}
          {source.cards && source.cards.length > 0 && (
            <span>{source.cards.length} cards extracted</span>
          )}
        </div>

        {/* Tags */}
        {source.tags && source.tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-3">
            {source.tags.map((tag) => (
              <span key={tag} className="text-xs text-muted-foreground">
                #{tag}
              </span>
            ))}
          </div>
        )}
      </header>

      {/* Content */}
      <div className="flex-1 overflow-auto px-4 py-4 pb-8">
        <div className="prose prose-sm dark:prose-invert max-w-none">
          <h3 className="text-sm font-medium mb-2">Source Content</h3>
          <div className="bg-muted/30 rounded-lg p-4 text-sm whitespace-pre-wrap font-mono leading-relaxed max-h-80 overflow-auto border border-border">
            {source.raw_text}
          </div>
        </div>

        {/* Cards from this source */}
        {source.cards && source.cards.length > 0 && (
          <div className="mt-6">
            <h3 className="text-sm font-medium mb-3 flex items-center gap-2">
              <LinkIcon className="h-4 w-4 text-muted-foreground" />
              Cards from this Source ({source.cards.length})
            </h3>
            <div className="space-y-2">
              {source.cards.map((card) => (
                <button
                  key={card.card_id}
                  onClick={() => onCardClick(card.card_id)}
                  className="w-full text-left p-3 border border-border rounded-lg hover:bg-muted/30 transition-colors"
                >
                  <div className="text-sm font-medium text-primary hover:underline">
                    {card.title || 'Untitled'}
                  </div>
                  <div className="text-xs text-muted-foreground line-clamp-2 mt-1">
                    {card.summary}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
